import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import MovimientoFormulario from "./MovimientoFormulario";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { actualizarMovimientoExtracto, getBancosExtracto, getMovimientoEliminable } from "../services/egresosService";

const num = (v) => { const n = Number(String(v ?? "").replace(/,/g, "")); return Number.isFinite(n) ? n : 0; };
const redondear = (n) => String(Math.round(n * 100) / 100);

// Datos del formulario a partir de la fila de la tabla (el débito se guarda sumado al ITF: se resta para volver al monto)
const desdeFila = (r) => {
  const esDebito = String(r.debito ?? "") !== "";
  return {
    tipo: esDebito ? "debito" : "credito",
    fecha: String(r.fecha || "").slice(0, 10),
    monto: esDebito ? redondear(num(r.debito) - num(r.itf)) : redondear(num(r.credito)),
    itf: esDebito && num(r.itf) ? String(r.itf) : "",
    banco: r.banco || "",
    cod_transaccion: r.cod_transaccion || "",
    referencia: r.referencia || "",
    descripcion: r.descripcion || "",
    adicional: r.adicional || "",
  };
};

// Editar un movimiento del extracto. Descripción, referencia y dato adicional siempre se pueden corregir; la fecha, el monto, el banco y el
// código solo si el movimiento no tiene relaciones (cheques, pagos, traspasos…): si las tiene, esos campos aparecen bloqueados.
export default function EditarMovimientoDialog({ movimiento, onClose, onGuardado, onVerRelaciones }) {
  const [form, setForm] = useState(null);
  const [relaciones, setRelaciones] = useState(null); // { eliminable, motivos }
  const [bancos, setBancos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!movimiento) return undefined;
    let activo = true;
    setForm(desdeFila(movimiento));
    setRelaciones(null);
    setError("");
    getMovimientoEliminable(movimiento.id).then(({ data }) => activo && setRelaciones(data?.data ?? data)).catch(() => activo && setRelaciones({ eliminable: false, motivos: ["relaciones que no se pudieron comprobar"] }));
    getBancosExtracto().then(({ data }) => activo && setBancos(Array.isArray(data) ? data : [])).catch(() => activo && setBancos([]));
    return () => { activo = false; };
  }, [movimiento]);

  if (!movimiento || !form) return null;

  const bloqueado = relaciones ? !relaciones.eliminable : true; // mientras se comprueba, se mantiene bloqueado
  const esDebito = form.tipo === "debito";

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      // Con relaciones solo se mandan los campos libres; el resto no se toca
      const cuerpo = bloqueado
        ? { descripcion: form.descripcion, referencia: form.referencia, adicional: form.adicional }
        : { ...form, itf: esDebito ? form.itf : "" };
      await actualizarMovimientoExtracto(movimiento.id, cuerpo);
      onGuardado({ id: movimiento.id });
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "No se pudo guardar el movimiento.");
    } finally {
      setGuardando(false);
    }
  };

  const estadoTxt = movimiento.estado === "No existe" ? "Pendiente" : movimiento.estado;
  const colorEstado = movimiento.estado === "Correcto" ? "success" : movimiento.estado === "No existe" ? "warning" : "primary";

  return (
    <Dialog open onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={guardar} scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1.25, borderBottom: 1, borderColor: "divider" }}>
        <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Editar movimiento</Typography>
        <Chip size="small" variant="outlined" label={`N.º ${movimiento.id}`} sx={{ fontWeight: 700 }} />
        <Chip size="small" color={colorEstado} label={estadoTxt} sx={{ fontWeight: 700 }} />
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "background.default", p: 1.5, pt: "16px !important", display: "grid", gridAutoRows: "max-content", gap: 1.25, alignContent: "start" }}>
        {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
        {relaciones && bloqueado && (
          <Alert severity="warning" icon={<LockOutlinedIcon fontSize="small" />} sx={{ py: 0.25, alignItems: "center" }}>
            Tiene <b>{(relaciones.motivos || []).join(", ")}</b>. La fecha, el monto, el banco y el código están <b>bloqueados</b>; solo puedes corregir la referencia,
            la descripción y el dato adicional. Para cambiar lo demás, desvincúlalo primero.
            {onVerRelaciones && (
              <Box sx={{ my: 0.75 }}>
                <Button size="small" variant="outlined" color="warning" startIcon={<LinkOffIcon />} onClick={() => onVerRelaciones(movimiento.id)} sx={{ textTransform: "none", fontWeight: 700 }}>
                  Ver relaciones y desvincular
                </Button>
              </Box>
            )}
          </Alert>
        )}
        {relaciones && !bloqueado && (
          <Alert severity="info" sx={{ py: 0 }}>Sin relaciones: puedes corregir todos los datos de este movimiento.</Alert>
        )}
        <MovimientoFormulario form={form} setForm={setForm} bancos={bancos} bloqueado={bloqueado} />
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={guardando || !relaciones} sx={{ textTransform: "none", fontWeight: 700, minWidth: 150 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
